import { useEffect, useState } from 'react';
import api from '../../../services/api';
import type { BoardroomReportBlock, BoardroomReportCard, BoardroomReportKey } from '../../../types';
import DownloadCsv from './DownloadCsv';
import ListCardReport from './ListCardReport';
import CountBlock from './blocks/CountBlock';
import { countBlockCsv, tableBlockCsv } from './blocks/blockCsv';
import useBoardRefresh from './useBoardRefresh';

/** A file name for one block: the report's name and the block's, made safe. */
function fileName(report: string, block: string): string {
  const slug = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  return `${slug(report) || 'report'}-${slug(block) || 'block'}.csv`;
}

export function ReportBlock({
  block,
  reportTitle,
  onOpen,
  onPickTag,
  tags,
}: {
  block: BoardroomReportBlock;
  reportTitle: string;
  onOpen?: (id: number) => void;
  /** A tag badge in a table block clicked: put it in the lens, or take it out. */
  onPickTag?: (key: string) => void;
  /** The tags in the lens now. */
  tags?: string[];
}) {
  return (
    <section className="min-w-0 space-y-1.5" data-testid="report-block">
      <div className="flex items-center gap-2">
        <h4 className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
          {block.title}
        </h4>
        {block.n > 0 && (
          <DownloadCsv
            csv={() =>
              block.type === 'table' ? tableBlockCsv(block.data) : countBlockCsv(block.data.bars)
            }
            filename={fileName(reportTitle, block.title)}
            label="CSV"
          />
        )}
      </div>
      {/* WHAT IT REALLY SHOWS, in the builder's own words — so a title that
          promises more than its filter does can be spotted by a reader too. */}
      {block.shows && (
        <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="block-shows">
          {block.shows}
          {block.unknown_fields?.length
            ? ` · no row has ${block.unknown_fields.join(', ')}`
            : ''}
        </p>
      )}
      {/* THIN DATA SAYS SO, above the block rather than under it, so it is read
          before the chart is. */}
      {block.note && (
        <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="thin-note">
          {block.note}
        </p>
      )}
      {block.type === 'table' ? (
        <ListCardReport data={block.data} onOpen={onOpen} onPickTag={onPickTag} lensTags={tags} />
      ) : (
        <CountBlock bars={block.data.bars} chart={block.spec.chart} onOpen={onOpen} />
      )}
    </section>
  );
}

/**
 * A report the room was asked for: several blocks, drawn in order.
 *
 * Fetches for itself, like `ListCardPane`, because two report cards share one
 * kind and only the subject tells them apart. Refetches while a dig is landing
 * findings, at most every 15 seconds.
 */
export default function ReportCardPane({
  boardroomId,
  subject,
  tags,
  verified,
  onOpen,
  onPickTag,
  refreshKey,
}: {
  boardroomId: number;
  subject: string;
  tags?: string[];
  verified?: string | null;
  onOpen?: (id: number) => void;
  /** A tag badge clicked: put it in the lens, or take it out. */
  onPickTag?: (key: string) => void;
  /** Changes whenever the page wants a refetch — the Reports tab passes the
   *  same trigger its built-in tables use, so this keeps pace with them. With
   *  none, the pane listens for itself (at most every 15 seconds). */
  refreshKey?: string | number;
}) {
  const [data, setData] = useState<BoardroomReportCard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lensKey = (tags ?? []).join('|');
  const fresh = useBoardRefresh(boardroomId, refreshKey === undefined);

  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .getBoardroomReport<BoardroomReportCard>(boardroomId, 'report' as BoardroomReportKey, {
        subject,
        tags: tags ?? [],
        verified: verified ?? null,
      })
      .then(d => {
        if (alive) setData(d);
      })
      .catch(() => {
        if (alive) setError('This report is no longer pinned to the Dashboard.');
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardroomId, subject, lensKey, verified, fresh, refreshKey]);

  if (error) {
    return <div className="text-xs text-gray-500 dark:text-gray-400">{error}</div>;
  }
  if (!data) {
    return <div className="text-xs text-gray-400 dark:text-gray-500">Reading the board…</div>;
  }
  const title = data.title || 'Report';
  return (
    <div className="space-y-5">
      {(data.spec.suggested_by || data.scoped) && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {data.spec.suggested_by ? `Suggested by the ${data.spec.suggested_by.replace(/_/g, ' ')}` : ''}
          {data.spec.suggested_by && data.spec.why ? ` - ${data.spec.why}` : ''}
          {data.spec.suggested_by && data.scoped ? ' · ' : ''}
          {data.scoped ? 'Cut by the lens you have on' : ''}
        </p>
      )}
      {data.truncated && (
        <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="report-truncated">
          This room has more findings than a report reads at once, so the oldest
          are left out of these counts.
        </p>
      )}
      {data.blocks.map((block, i) => (
        <ReportBlock key={`${i}:${block.title}`} block={block} reportTitle={title} onOpen={onOpen} onPickTag={onPickTag} tags={tags} />
      ))}
      {!data.blocks.length && (
        <div className="text-xs text-gray-500 dark:text-gray-400">This report has no blocks.</div>
      )}
    </div>
  );
}
